import { getTheme } from '@fluentui/react';
import {
  AreaChart,
  IChartProps,
  ILineChartPoints,
  ILineDataInVerticalBarChart,
  IVerticalBarChartDataPoint,
  IVerticalStackedChartProps,
  LineChart,
  VerticalBarChart,
  VerticalStackedBarChart,
} from '@fluentui/react-charting';
import { IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import KPI, { KPIAggregationMethods, KPIChartTypes } from 'models/kpi/kpi';
import { KPIGraphDataPoint, KPIGraphDataStackedPoint } from 'models/kpi/kpiGraphData';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toLocaleDateShort } from 'utils/datetime';

interface IKPIDataViewProps {
  kpi: KPI | undefined;
  aggregationMethod?: KPIAggregationMethods;
  chartType: KPIChartTypes;
  isLoading: boolean;
}

//
// Constants
//
const yMin: number = 0.0001;
const defAvgLineColor = '#e8af33';

//
// Static helper functions
//
const getSumOfValues = (values: { [key: string]: number }): number => {
  let sum: number = 0;

  Object.keys(values).forEach((v) => {
    sum += values[v];
  });

  return sum;
};

const getMaxValue = (data: number[]): number => {
  if (data.length === 0) return 0;

  return Math.max(...data);
};

const getMinValue = (data: number[]): number => {
  if (data.length === 0) return 0;

  return Math.min(...data);
};

//
// Component
//
const KPIDataView = (props: IKPIDataViewProps) => {
  const { t } = useTranslation(['translation', 'kpi']);
  const theme = getTheme();
  const defChartColor = theme.palette.themePrimary;

  const max = useMemo(() => getMaxValue(props.kpi?.graphData?.points.map((p) => p.value ?? 0) ?? []), [props.kpi]);
  const min = useMemo(() => getMinValue(props.kpi?.graphData?.points.map((p) => p.value ?? 0) ?? []), [props.kpi]);

  const getDataVerticalBarChart = (data: KPIGraphDataPoint[] | undefined): IVerticalBarChartDataPoint[] => {
    if (!data || data.length === 0) return [];

    let lineData: ILineDataInVerticalBarChart | undefined = undefined;
    if (props.aggregationMethod === KPIAggregationMethods.Count) {
      const average = data.reduce((p, c) => p + (c.value ?? 0), 0) / data.length;
      lineData = { y: average };
    }

    return data.map((d) => {
      const item: IVerticalBarChartDataPoint = {
        x: toLocaleDateShort(d.date),
        y: d.value || yMin,
        lineData: lineData,
      };

      return item;
    });
  };

  const getDataLineChartChart = (data: KPIGraphDataPoint[] | undefined): IChartProps => {
    if (!data || data.length === 0) return { lineChartData: [] };

    const points: ILineChartPoints[] = [
      {
        data: data.map((d) => {
          return {
            x: d.date || 0,
            y: d.value || yMin,
          };
        }),
        legend: '',
        color: defChartColor,
      },
    ];

    return { lineChartData: points };
  };

  const getDataStackedBarChart = (data: KPIGraphDataStackedPoint[] | undefined): IVerticalStackedChartProps[] => {
    if (!data || data.length === 0) return [];

    const average = data.reduce((p, c) => p + getSumOfValues(c.values), 0) / data.length;

    const output: IVerticalStackedChartProps[] = data.map((d) => {
      const point: IVerticalStackedChartProps = {
        chartData: Object.keys(d.values).map((v) => {
          return {
            legend: v,
            data: d.values[v] || yMin,
          };
        }),
        lineData:
          props.aggregationMethod === KPIAggregationMethods.Count
            ? Object.keys(d.values).map((v) => {
                return {
                  y: average || yMin,
                  legend: '',
                  color: defAvgLineColor,
                };
              })
            : undefined,
        xAxisPoint: toLocaleDateShort(d.date) || 0,
      };

      return point;
    });

    return output;
  };

  const NoDataView = (): JSX.Element => {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('kpi:TabData.NoData')}</IPText>
      </IPStack>
    );
  };

  //
  // Main render
  //
  if (props.isLoading || !props.kpi) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  // if all values are the same or zero, the charts look better with some range in y axis
  let yMaxValue = max * 1.1;
  let yMinValue = min * 1.1;

  // if max and min are 0, set y axis from 0 to 10
  if (max === 0 && min === 0) {
    yMaxValue = 10;
    yMinValue = 0;
  }

  if (yMinValue > 0) {
    yMinValue = 0;
  }

  if (yMaxValue < 0) {
    yMaxValue = 0;
  }

  switch (props.chartType) {
    case KPIChartTypes.Bar:
      if (props.aggregationMethod === KPIAggregationMethods.Count && props.kpi.isStackedType()) {
        const stackedData = getDataStackedBarChart(props.kpi.graphData?.stackedPoints);
        if (stackedData?.length === 0) return NoDataView();

        return (
          <VerticalStackedBarChart
            data={stackedData}
            hideLegend={true}
            barMinimumHeight={3}
            yMaxValue={yMaxValue}
            yMinValue={yMinValue}
          />
        );
      } else {
        const barData = getDataVerticalBarChart(props.kpi.graphData?.points);
        if (barData?.length === 0) return NoDataView();

        return (
          <VerticalBarChart
            data={barData}
            wrapXAxisLables={true}
            hideLegend={true}
            enableGradient={true}
            roundCorners={true}
            yMaxValue={yMaxValue}
            yMinValue={-yMinValue}
          />
        );
      }
    case KPIChartTypes.Line:
      const lineData = getDataLineChartChart(props.kpi.graphData?.points);
      if (lineData.lineChartData?.length === 0) return NoDataView();

      return <LineChart data={lineData} wrapXAxisLables={true} hideLegend={true} />;
    case KPIChartTypes.Area:
      const areaData = getDataLineChartChart(props.kpi.graphData?.points);
      if (areaData.lineChartData?.length === 0) return NoDataView();

      return <AreaChart data={areaData} wrapXAxisLables={true} hideLegend={true} />;
    default:
      return null;
  }
};

export default KPIDataView;
